import { titleize } from 'inflected';
import { FilterColumn } from '@blisspointmedia/bpm-types/dist/FilterBar';
import {
  ActivityKind,
  AnyActivity,
  Event,
  EvidenceKind,
  isEventType,
  isImportance,
  isOrigin,
  Origin
} from '@blisspointmedia/bpm-types/dist/OracleActivity';
import { CheckState, CheckStateType } from '@Components/designConstants';
import { BulkAction, BulkActionType } from '../activity-feed-constants';
import { ActivityId } from '../activity-feed-data-utils';
import { getImportanceDisplay, getProvenanceDisplay } from '../activity-feed-utils';
import { getEventTheme } from '../oracle-taxonomy-utils';

const ROW_HEIGHT_CARD = 103;
const ROW_HEIGHT_DIVIDER = 40;
const ROW_HEIGHT_FOOTER = 56;

export const TAIL_PREFETCH_ROWS = 30;

// Typed against `keyof Event` so a bpm-types rename breaks the build. Fields a kind doesn't carry
// yield no values.
const SEARCHABLE_EVENT_FIELDS: (keyof Event)[] = [
  'title',
  'details',
  'eventType',
  'channel',
  'categoryTags',
  'signalTag',
  'source',
  'rationale',
  'recommendedAction',
  'cid'
];

const toSearchValues = (value: unknown): string[] => {
  if (typeof value === 'string') return value ? [value] : [];
  if (Array.isArray(value)) return value.flatMap(toSearchValues);
  return [];
};

const isQuoteEvidence = (item: unknown): item is { quote: unknown } =>
  typeof item === 'object' &&
  item !== null &&
  'kind' in item &&
  item.kind === EvidenceKind.QUOTE &&
  'quote' in item;

const toQuoteTexts = (evidence: unknown): string[] => {
  if (!Array.isArray(evidence)) return [];
  return evidence.flatMap(item => (isQuoteEvidence(item) ? toSearchValues(item.quote) : []));
};

const searchColumn = (
  name: string,
  getValues: (line: Record<string, unknown>) => string[]
): FilterColumn => ({ name, label: name, optionsGetter: getValues });

// Explicit getters, because FilterBar's default getter stringifies the raw value: a null field
// would match a search for "null", and an array or object would not match its contents.
export const ACTIVITY_SEARCH_COLUMNS: FilterColumn[] = [
  ...SEARCHABLE_EVENT_FIELDS.map(field => searchColumn(field, line => toSearchValues(line[field]))),
  searchColumn('quotes', line => toQuoteTexts(line.evidence)),
  // The UI shows these derived labels, not the stored values.
  searchColumn('theme', line =>
    isEventType(line.eventType) ? [getEventTheme(line.eventType)] : []
  ),
  searchColumn('priority', line =>
    isImportance(line.importance) ? [getImportanceDisplay(line.importance).label] : []
  ),
  searchColumn('provenance', line =>
    isOrigin(line.origin) ? [getProvenanceDisplay(line.origin).label] : []
  ),
  searchColumn('client', line => (typeof line.cid === 'string' ? [titleize(line.cid)] : []))
];

export type VirtualRow =
  | { kind: 'divider'; key: string; label: string }
  | { kind: 'card'; key: string; activityId: ActivityId }
  | { kind: 'footer'; key: 'footer' };

export const rowHeightFor = (row: VirtualRow): number => {
  if (row.kind === 'divider') return ROW_HEIGHT_DIVIDER;
  if (row.kind === 'footer') return ROW_HEIGHT_FOOTER;
  return ROW_HEIGHT_CARD;
};

// True when the user has scrolled within the prefetch window of the tail on a list that's
// larger than the window itself. Below-window-size lists satisfy the "within N of the tail"
// distance check on every render, so they're covered by the small-N gate in the caller's
// empty-tail useEffect instead — this predicate is scroll-based, not size-based.
export const shouldPrefetchOnScroll = (params: {
  rowCount: number;
  stopIndex: number;
  hasNextPage: boolean;
  isFetchingNextPage: boolean;
}): boolean => {
  const { rowCount, stopIndex, hasNextPage, isFetchingNextPage } = params;
  return (
    hasNextPage &&
    !isFetchingNextPage &&
    rowCount > TAIL_PREFETCH_ROWS &&
    stopIndex >= rowCount - TAIL_PREFETCH_ROWS
  );
};

export const getCheckState = (checkedCount: number, totalCount: number): CheckStateType => {
  if (checkedCount === 0) return CheckState.UNCHECKED;
  if (checkedCount < totalCount) return CheckState.INDETERMINATE;
  return CheckState.CHECKED;
};

export const toggleCheckedId = (
  checkedIds: ReadonlySet<ActivityId>,
  id: ActivityId
): ReadonlySet<ActivityId> => {
  const next = new Set(checkedIds);
  if (next.has(id)) {
    next.delete(id);
  } else {
    next.add(id);
  }
  return next;
};

// Returns the same set when nothing was pruned so a setState bail-out skips the re-render.
export const pruneCheckedIds = (
  checkedIds: ReadonlySet<ActivityId>,
  visibleIds: readonly ActivityId[]
): ReadonlySet<ActivityId> => {
  const visible = new Set(visibleIds);
  const kept = [...checkedIds].filter(id => visible.has(id));
  return kept.length === checkedIds.size ? checkedIds : new Set(kept);
};

export interface BulkActionItem {
  action: BulkActionType;
  disabled: boolean;
}

interface BulkActionContext {
  showWip: boolean;
  checkedActivities: AnyActivity[];
}

export const getBulkActions = ({
  showWip,
  checkedActivities
}: BulkActionContext): BulkActionItem[] => {
  const download = { action: BulkAction.DOWNLOAD, disabled: false };
  if (!showWip) return [download];

  const hasTasks = checkedActivities.some(activity => activity.kind === ActivityKind.TASK);
  const allManual =
    checkedActivities.length > 0 &&
    checkedActivities.every(activity => activity.origin === Origin.MANUAL);
  return [
    { action: BulkAction.STAR, disabled: false },
    { action: BulkAction.PIN, disabled: false },
    { action: BulkAction.SHARE, disabled: false },
    { action: BulkAction.HIDE, disabled: false },
    { action: BulkAction.MARK_TASKS_DONE, disabled: !hasTasks },
    download,
    ...(allManual ? [{ action: BulkAction.DELETE, disabled: false }] : [])
  ];
};
